import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Chart, registerables } from 'chart.js';
import { orderService } from '@/services/order.service';
import { clientService } from '@/services/client.service';
import { analyticsService } from '@/services/analytics.service';
import { productService } from '@/services/product.service';
import { formatPrice } from '@/utils/formatters';
import {
  CurrencyDollarIcon,
  ShoppingCartIcon,
  UserGroupIcon,
  ChartBarIcon,
  ShoppingBagIcon,
  ArrowPathIcon,
  ClockIcon,
  DocumentTextIcon,
} from '@heroicons/react/24/outline';
import '@/styles/dashboard.css';

Chart.register(...registerables);

export const Rapport = () => {
  const { t } = useTranslation();
  // Références pour les 8 graphiques
  const salesChartRef = useRef<HTMLCanvasElement>(null);
  const salesDistChartRef = useRef<HTMLCanvasElement>(null);
  const ordersStatusChartRef = useRef<HTMLCanvasElement>(null);
  const topProductsChartRef = useRef<HTMLCanvasElement>(null);
  const revenueChartRef = useRef<HTMLCanvasElement>(null);
  const conversionChartRef = useRef<HTMLCanvasElement>(null);
  const cartValueChartRef = useRef<HTMLCanvasElement>(null);
  const sessionsChartRef = useRef<HTMLCanvasElement>(null);
  
  // Instances des graphiques
  const salesChartInstance = useRef<Chart | null>(null);
  const salesDistChartInstance = useRef<Chart | null>(null);
  const ordersStatusChartInstance = useRef<Chart | null>(null);
  const topProductsChartInstance = useRef<Chart | null>(null);
  const revenueChartInstance = useRef<Chart | null>(null);
  const conversionChartInstance = useRef<Chart | null>(null);
  const cartValueChartInstance = useRef<Chart | null>(null);
  const sessionsChartInstance = useRef<Chart | null>(null);

  const [activePeriod, setActivePeriod] = useState('7j');
  const [customDateFrom, setCustomDateFrom] = useState('');
  const [customDateTo, setCustomDateTo] = useState('');
  const [customDateFromInput, setCustomDateFromInput] = useState('');
  const [customDateToInput, setCustomDateToInput] = useState('');
  const [filterError, setFilterError] = useState('');
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalSales: 0,
    totalOrders: 0,
    totalUsers: 0,
    conversionRate: 0,
    averageCart: 0,
    bounceRate: 0,
    averageSessionTime: 0,
    pageViews: 0,
  });
  const [orders, setOrders] = useState<any[]>([]);
  const [clients, setClients] = useState<any[]>([]);
  const [analyticsKPIs, setAnalyticsKPIs] = useState<any>(null);
  const [timeSeries, setTimeSeries] = useState<any[]>([]);
  const [sessions, setSessions] = useState<any[]>([]);

  const colors = {
    primary: '#8C1843',
    success: '#4CAF50',
    warning: '#FF9800',
    error: '#F44336',
    info: '#2196F3',
    lightGrey: '#F5F5F5'
  };

  // Charger les données réelles
  useEffect(() => {
    loadData();
  }, [activePeriod, customDateFrom, customDateTo]);

  const getDateRange = () => {
    const now = new Date();
    const dateTo = now.toISOString().split('T')[0];
    let dateFrom: string;
    
    if (activePeriod === 'custom' && customDateFrom && customDateTo) {
      return { dateFrom: customDateFrom, dateTo: customDateTo };
    }

    switch (activePeriod) {
      case '7j':
        const sevenDaysAgo = new Date(now);
        sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
        dateFrom = sevenDaysAgo.toISOString().split('T')[0];
        break;
      case '30j':
        const thirtyDaysAgo = new Date(now);
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
        dateFrom = thirtyDaysAgo.toISOString().split('T')[0];
        break;
      case '90j':
        const ninetyDaysAgo = new Date(now);
        ninetyDaysAgo.setDate(ninetyDaysAgo.getDate() - 90);
        dateFrom = ninetyDaysAgo.toISOString().split('T')[0];
        break;
      default:
        dateFrom = dateTo;
    }
    
    return { dateFrom, dateTo };
  };

  const applyCustomRange = () => {
    if (!customDateFromInput || !customDateToInput) {
      setFilterError(t('adminReport.filterErrorDateRequired'));
      return;
    }

    if (customDateFromInput > customDateToInput) {
      setFilterError(t('adminReport.filterErrorDateOrder'));
      return;
    }

    setFilterError('');
    setCustomDateFrom(customDateFromInput);
    setCustomDateTo(customDateToInput);
    setActivePeriod('custom');
  };

  const resetFilters = () => {
    setFilterError('');
    setCustomDateFrom('');
    setCustomDateTo('');
    setCustomDateFromInput('');
    setCustomDateToInput('');
    setActivePeriod('7j');
  };

  const loadData = async () => {
    try {
      setLoading(true);
      const { dateFrom, dateTo } = getDateRange();

      // Charger toutes les données en parallèle
      // Pour les statistiques globales, charger toutes les commandes (sans filtre de date)
      // Pour les graphiques, utiliser les données filtrées par période
      const [allOrdersData, ordersData, clientsData, kpisDataAll, kpisDataFiltered, timeSeriesData, sessionsData] = await Promise.all([
        orderService.getAll(), // Toutes les commandes pour les stats globales
        orderService.getAll({ date_from: dateFrom, date_to: dateTo }), // Commandes filtrées pour les graphiques
        clientService.getAll(),
        analyticsService.getKPIs(), // KPIs globaux (sans filtre de date)
        analyticsService.getKPIs(dateFrom, dateTo), // KPIs filtrés pour les graphiques
        analyticsService.getTimeSeries(dateFrom, dateTo),
        analyticsService.getSessions(dateFrom, dateTo, 1000),
      ]);

      // Si aucune commande n'est trouvée dans la période, utiliser toutes les commandes pour les graphiques
      // Cela permet d'afficher les données même si elles sont dans une période différente (ex: données de démo en 2025)
      if (ordersData.length === 0 && allOrdersData.length > 0) {
        console.log('⚠️ Aucune commande trouvée dans la période sélectionnée. Affichage de toutes les commandes.');
        setOrders(allOrdersData);
      } else {
        setOrders(ordersData); // Utiliser les commandes filtrées pour les graphiques
      }
      
      setClients(clientsData);
      setAnalyticsKPIs(kpisDataFiltered); // Utiliser les KPIs filtrés pour les graphiques
      setTimeSeries(timeSeriesData);
      setSessions(sessionsData);

      // Utiliser les KPIs GLOBAUX (sans filtre de date) pour les statistiques des KPI cards
      // Si les KPIs ne sont pas disponibles, calculer à partir de toutes les commandes
      
      // Calculer les ventes totales
      let totalSales = 0;
      if (kpisDataAll?.paidOrdersTotal !== undefined && kpisDataAll?.paidOrdersTotal !== null) {
        totalSales = parseFloat(kpisDataAll.paidOrdersTotal) || 0;
      } else {
        const paidOrders = allOrdersData.filter(o => {
          const status = o.payment_status || o.paymentStatus;
          return status === 'paid' || status === 'Paid' || status === 'PAID';
        });
        totalSales = paidOrders.reduce((sum, o) => {
          const total = parseFloat(o.total) || parseFloat(o.total_amount) || 0;
          return sum + total;
        }, 0);
      }
      
      // Calculer le nombre de commandes payées
      let totalOrders = 0;
      if (kpisDataAll?.paidOrders !== undefined && kpisDataAll?.paidOrders !== null) {
        totalOrders = parseInt(kpisDataAll.paidOrders) || 0;
      } else {
        totalOrders = allOrdersData.filter(o => {
          const status = o.payment_status || o.paymentStatus;
          return status === 'paid' || status === 'Paid' || status === 'PAID';
        }).length;
      }
      
      const totalUsers = clientsData?.length || 0;
      
      // Pour le taux de conversion, utiliser les KPIs filtrés (de la période sélectionnée)
      const conversionRate = parseFloat(kpisDataFiltered?.conversionRate) || 0;
      
      // Utiliser avgOrderValue des KPIs globaux si disponible, sinon calculer
      let averageCart = 0;
      if (kpisDataAll?.avgOrderValue !== undefined && kpisDataAll?.avgOrderValue !== null) {
        averageCart = parseFloat(kpisDataAll.avgOrderValue) || 0;
      } else if (totalOrders > 0) {
        averageCart = totalSales / totalOrders;
      }
      
      // Calculer le taux de rebond (sessions avec une seule page vue / total sessions)
      const totalSessions = sessionsData?.length || 0;
      const singlePageSessions = sessionsData?.filter(s => {
        const pageCount = s.pages?.length || 0;
        return pageCount <= 1;
      }).length || 0;
      const bounceRate = totalSessions > 0 ? (singlePageSessions / totalSessions) * 100 : 0;
      
      // Calculer le temps moyen de session (basé sur les sessions)
      // Utiliser first_visit et last_visit directement de la session
      let validSessions = 0;
      const totalSessionTime = (sessionsData || []).reduce((sum, s) => {
        if (s?.first_visit && s?.last_visit) {
          try {
            const start = new Date(s.first_visit).getTime();
            const end = new Date(s.last_visit).getTime();
            
            if (!isNaN(start) && !isNaN(end) && end > start) {
              const duration = (end - start) / 1000; // en secondes
              // Limiter à 30 minutes max par session (1800 secondes) pour éviter les valeurs aberrantes
              // Les sessions très longues sont probablement des erreurs ou des sessions inactives
              if (duration <= 1800 && duration >= 0) {
                validSessions++;
                return sum + duration;
              }
            }
          } catch (e) {
            // Ignorer les erreurs de parsing
          }
        }
        return sum;
      }, 0);
      
      const averageSessionTime = validSessions > 0 ? totalSessionTime / validSessions : 0;
      
      // Calculer les pages vues totales
      const pageViews = (kpisDataAll?.pageViews !== undefined && kpisDataAll?.pageViews !== null) 
        ? parseInt(kpisDataAll.pageViews) || 0
        : (kpisDataFiltered?.pageViews !== undefined && kpisDataFiltered?.pageViews !== null)
        ? parseInt(kpisDataFiltered.pageViews) || 0
        : (sessionsData || []).reduce((sum, s) => sum + (s.pages?.length || 0), 0);

      // Log pour débogage
      const ordersToDisplay = (ordersData.length === 0 && allOrdersData.length > 0 ? allOrdersData : ordersData);
      console.log('📊 Données chargées:', {
        allOrdersCount: allOrdersData?.length || 0,
        filteredOrdersCount: ordersData?.length || 0,
        ordersToDisplayCount: ordersToDisplay?.length || 0,
        dateRange: { dateFrom, dateTo },
        activePeriod,
        paidOrdersCount: allOrdersData?.filter(o => {
          const status = o.payment_status || o.paymentStatus;
          return status === 'paid' || status === 'Paid' || status === 'PAID';
        }).length || 0,
        kpisDataAll,
        kpisDataFiltered,
        totalSales,
        totalOrders,
        averageCart,
        conversionRate,
        bounceRate,
        averageSessionTime,
        pageViews,
        sessionsCount: sessionsData?.length || 0,
      });

      setStats({
        totalSales,
        totalOrders,
        totalUsers,
        conversionRate,
        averageCart,
        bounceRate,
        averageSessionTime,
        pageViews,
      });
    } catch (error) {
      console.error('Erreur lors du chargement des données:', error);
      console.error('Détails:', error);
    } finally {
      setLoading(false);
    }
  };

  // Créer/mettre à jour les 8 graphiques
  useEffect(() => {
    if (loading || !timeSeries.length) return;

    // Graphique 1: Ventes par période (ligne)
    if (salesChartRef.current) {
      const ctx = salesChartRef.current.getContext('2d');
      if (ctx) {
        if (salesChartInstance.current) {
          salesChartInstance.current.destroy();
        }
        const labels = timeSeries.map(d => {
          const date = new Date(d.date);
          return date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
        });
        // Calculer les ventes réelles par jour à partir des commandes
        const salesByDate: Record<string, number> = {};
        orders.forEach(order => {
          if (order.payment_status === 'paid' && order.created_at) {
            const date = new Date(order.created_at).toISOString().split('T')[0];
            salesByDate[date] = (salesByDate[date] || 0) + (order.total || 0);
          }
        });
        const salesData = timeSeries.map(d => salesByDate[d.date] || 0);
        
        salesChartInstance.current = new Chart(ctx, {
          type: 'line',
          data: {
            labels,
            datasets: [{
              label: t('adminReport.salesEur'),
              data: salesData,
              borderColor: colors.primary,
              backgroundColor: 'rgba(140, 24, 67, 0.1)',
              borderWidth: 3,
              fill: true,
              tension: 0.4,
              pointRadius: 5,
              pointBackgroundColor: colors.primary,
              pointBorderColor: '#fff',
              pointBorderWidth: 2
            }]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: { display: false },
              tooltip: {
                backgroundColor: 'rgba(0, 0, 0, 0.8)',
                padding: 12,
                callbacks: {
                  label: function(context) {
                    return context.parsed.y.toLocaleString('fr-FR') + ' €';
                  }
                }
              }
            },
            scales: {
              y: {
                beginAtZero: true,
                ticks: {
                  callback: function(value) {
                    return (Number(value) / 1000).toFixed(1) + 'k';
                  }
                },
                grid: { color: 'rgba(0, 0, 0, 0.05)' }
              },
              x: { grid: { display: false } }
            }
          }
        });
      }
    }
  }, [activePeriod, timeSeries, orders, loading, t]);

  useEffect(() => {
    if (loading || !orders.length) return;

    // Graphique 2: Répartition des ventes par statut de paiement (donut)
    if (salesDistChartRef.current) {
      const ctx = salesDistChartRef.current.getContext('2d');
      if (ctx) {
        if (salesDistChartInstance.current) {
          salesDistChartInstance.current.destroy();
        }
        const paid = orders.filter(o => o.payment_status === 'paid').length;
        const pending = orders.filter(o => o.payment_status === 'pending').length;
        const unpaid = orders.filter(o => o.payment_status === 'unpaid').length;
        const total = orders.length;
        
        salesDistChartInstance.current = new Chart(ctx, {
          type: 'doughnut',
          data: {
            labels: [t('adminReport.paid'), t('adminReport.pending'), t('adminReport.unpaid')],
            datasets: [{
              data: [
                total > 0 ? (paid / total) * 100 : 0,
                total > 0 ? (pending / total) * 100 : 0,
                total > 0 ? (unpaid / total) * 100 : 0,
              ],
              backgroundColor: [colors.success, colors.warning, colors.error],
              borderWidth: 0
            }]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: {
                position: 'bottom',
                labels: { padding: 15, usePointStyle: true }
              },
              tooltip: {
                callbacks: {
                  label: function(context) {
                    return context.label + ': ' + context.parsed.toFixed(1) + '%';
                  }
                }
              }
            }
          }
        });
      }
    }
  }, [orders, loading, t]);

  useEffect(() => {
    if (loading || !orders.length) return;

    // Graphique 3: Commandes par statut (barres)
    if (ordersStatusChartRef.current) {
      const ctx = ordersStatusChartRef.current.getContext('2d');
      if (ctx) {
        if (ordersStatusChartInstance.current) {
          ordersStatusChartInstance.current.destroy();
        }
        const delivered = orders.filter(o => o.order_status === 'delivered').length;
        const processing = orders.filter(o => o.order_status === 'processing').length;
        const pending = orders.filter(o => o.order_status === 'pending').length;
        const cancelled = orders.filter(o => o.order_status === 'cancelled').length;
        
        ordersStatusChartInstance.current = new Chart(ctx, {
          type: 'bar',
          data: {
            labels: [t('adminReport.delivered'), t('adminReport.inProgress'), t('adminReport.pending'), t('adminReport.cancelled')],
            datasets: [{
              label: t('adminReport.ordersCount'),
              data: [delivered, processing, pending, cancelled],
              backgroundColor: [colors.success, colors.info, colors.warning, colors.error],
              borderRadius: 8,
              borderSkipped: false
            }]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: { display: false },
              tooltip: {
                backgroundColor: 'rgba(0, 0, 0, 0.8)',
                padding: 12
              }
            },
            scales: {
              y: {
                beginAtZero: true,
                ticks: { stepSize: 1 },
                grid: { color: 'rgba(0, 0, 0, 0.05)' }
              },
              x: { grid: { display: false } }
            }
          }
        });
      }
    }
  }, [orders, loading, t]);

  useEffect(() => {
    if (loading || !orders.length) return;

    // Graphique 4: Top 5 produits (barres horizontales)
    if (topProductsChartRef.current) {
      const ctx = topProductsChartRef.current.getContext('2d');
      if (ctx) {
        if (topProductsChartInstance.current) {
          topProductsChartInstance.current.destroy();
        }
        
        // Calculer les ventes par produit
        const productSales: Record<string, number> = {};
        orders.forEach(order => {
          try {
            const items = typeof order.items === 'string' ? JSON.parse(order.items) : order.items;
            items.forEach((item: any) => {
              const productName = item.product_name || t('adminReport.unknownProduct');
              productSales[productName] = (productSales[productName] || 0) + (item.quantity || 0);
            });
          } catch (e) {
            console.error('Erreur parsing items:', e);
          }
        });
        
        const topProducts = Object.entries(productSales)
          .sort((a, b) => b[1] - a[1])
          .slice(0, 5);
        
        topProductsChartInstance.current = new Chart(ctx, {
          type: 'bar',
          data: {
            labels: topProducts.map(([name]) => name.length > 20 ? name.substring(0, 20) + '...' : name),
            datasets: [{
              label: t('adminReport.sales'),
              data: topProducts.map(([, count]) => count),
              backgroundColor: colors.primary,
              borderRadius: 8
            }]
          },
          options: {
            indexAxis: 'y',
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: { display: false },
              tooltip: {
                backgroundColor: 'rgba(0, 0, 0, 0.8)',
                padding: 12
              }
            },
            scales: {
              x: {
                beginAtZero: true,
                grid: { color: 'rgba(0, 0, 0, 0.05)' }
              },
              y: { grid: { display: false } }
            }
          }
        });
      }
    }
  }, [orders, loading, t]);

  useEffect(() => {
    if (loading || !timeSeries.length) return;

    // Graphique 5: Évolution des revenus (ligne)
    if (revenueChartRef.current) {
      const ctx = revenueChartRef.current.getContext('2d');
      if (ctx) {
        if (revenueChartInstance.current) {
          revenueChartInstance.current.destroy();
        }
        const labels = timeSeries.map(d => {
          const date = new Date(d.date);
          return date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
        });
        // Calculer les revenus réels par jour à partir des commandes
        const revenueByDate: Record<string, number> = {};
        orders.forEach(order => {
          if (order.payment_status === 'paid' && order.created_at) {
            const date = new Date(order.created_at).toISOString().split('T')[0];
            revenueByDate[date] = (revenueByDate[date] || 0) + (order.total || 0);
          }
        });
        const revenueData = timeSeries.map(d => revenueByDate[d.date] || 0);
        
        revenueChartInstance.current = new Chart(ctx, {
          type: 'line',
          data: {
            labels,
            datasets: [{
              label: t('adminReport.revenueEur'),
              data: revenueData,
              borderColor: colors.primary,
              backgroundColor: 'rgba(140, 24, 67, 0.1)',
              borderWidth: 3,
              fill: true,
              tension: 0.4,
              pointRadius: 4,
              pointBackgroundColor: colors.primary,
              pointBorderColor: '#fff',
              pointBorderWidth: 2
            }]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: { display: false },
              tooltip: {
                backgroundColor: 'rgba(0, 0, 0, 0.8)',
                padding: 12,
                callbacks: {
                  label: function(context) {
                    return context.parsed.y.toLocaleString('fr-FR') + ' €';
                  }
                }
              }
            },
            scales: {
              y: {
                beginAtZero: true,
                ticks: {
                  callback: function(value) {
                    return (Number(value) / 1000).toFixed(1) + 'k';
                  }
                },
                grid: { color: 'rgba(0, 0, 0, 0.05)' }
              },
              x: { grid: { display: false } }
            }
          }
        });
      }
    }

    // Graphique 6: Taux de conversion (ligne)
    if (conversionChartRef.current) {
      const ctx = conversionChartRef.current.getContext('2d');
      if (ctx) {
        if (conversionChartInstance.current) {
          conversionChartInstance.current.destroy();
        }
        const labels = timeSeries.map(d => {
          const date = new Date(d.date);
          return date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
        });
        const conversionData = timeSeries.map(d => {
          if (d.visitors === 0) return 0;
          return (
    <>
d.purchases / d.visitors
    </>
  ) * 100;
        });
        
        conversionChartInstance.current = new Chart(ctx, {
          type: 'line',
          data: {
            labels,
            datasets: [{
              label: t('adminReport.conversionPercent'),
              data: conversionData,
              borderColor: colors.success,
              backgroundColor: 'rgba(76, 175, 80, 0.1)',
              borderWidth: 2,
              fill: true,
              tension: 0.4,
            }]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: { display: false }
            },
            scales: {
              y: {
                beginAtZero: true,
                ticks: {
                  callback: function(value) {
                    return value.toFixed(1) + '%';
                  }
                }
              }
            }
          }
        });
      }
    }

    // Graphique 7: Valeur moyenne du panier (barres)
    if (cartValueChartRef.current) {
      const ctx = cartValueChartRef.current.getContext('2d');
      if (ctx) {
        if (cartValueChartInstance.current) {
          cartValueChartInstance.current.destroy();
        }
        const labels = timeSeries.map(d => {
          const date = new Date(d.date);
          return date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
        });
        // Calculer la valeur moyenne du panier par jour
        const cartValuesByDate: Record<string, { total: number; count: number }> = {};
        orders.forEach(order => {
          if (order.payment_status === 'paid' && order.created_at) {
            const date = new Date(order.created_at).toISOString().split('T')[0];
            if (!cartValuesByDate[date]) {
              cartValuesByDate[date] = { total: 0, count: 0 };
            }
            cartValuesByDate[date].total += (order.total || 0);
            cartValuesByDate[date].count += 1;
          }
        });
        const cartValues = timeSeries.map(d => {
          const dayData = cartValuesByDate[d.date];
          if (dayData && dayData.count > 0) {
            return dayData.total / dayData.count;
          }
          return 0;
        });
        
        cartValueChartInstance.current = new Chart(ctx, {
          type: 'bar',
          data: {
            labels,
            datasets: [{
              label: t('adminReport.avgCartEur'),
              data: cartValues,
              backgroundColor: colors.info,
              borderRadius: 8,
            }]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: { display: false }
            },
            scales: {
              y: {
                beginAtZero: true,
                ticks: {
                  callback: function(value) {
                    return value.toLocaleString('fr-FR') + ' €';
                  }
                }
              }
            }
          }
        });
      }
    }

    // Graphique 8: Sessions et visiteurs (barres groupées)
    if (sessionsChartRef.current) {
      const ctx = sessionsChartRef.current.getContext('2d');
      if (ctx) {
        if (sessionsChartInstance.current) {
          sessionsChartInstance.current.destroy();
        }
        const labels = timeSeries.map(d => {
          const date = new Date(d.date);
          return date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
        });
        
        sessionsChartInstance.current = new Chart(ctx, {
          type: 'bar',
          data: {
            labels,
            datasets: [
              {
                label: t('adminReport.visitors'),
                data: timeSeries.map(d => d.visitors),
                backgroundColor: colors.primary,
              },
              {
                label: t('adminReport.cartsCreated'),
                data: timeSeries.map(d => d.carts_created),
                backgroundColor: colors.warning,
              }
            ]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: { display: true, position: 'top' }
            },
            scales: {
              y: {
                beginAtZero: true
              }
            }
          }
        });
      }
    }

    return () => {
      if (salesChartInstance.current) salesChartInstance.current.destroy();
      if (salesDistChartInstance.current) salesDistChartInstance.current.destroy();
      if (ordersStatusChartInstance.current) ordersStatusChartInstance.current.destroy();
      if (topProductsChartInstance.current) topProductsChartInstance.current.destroy();
      if (revenueChartInstance.current) revenueChartInstance.current.destroy();
      if (conversionChartInstance.current) conversionChartInstance.current.destroy();
      if (cartValueChartInstance.current) cartValueChartInstance.current.destroy();
      if (sessionsChartInstance.current) sessionsChartInstance.current.destroy();
    };
  }, [timeSeries, orders, stats, loading, activePeriod, t]);

  return (
    <>

    <div className="advanced-filter-card">
              <div className="advanced-filter-header">
                <h2>{t('adminReport.advancedFilter')}</h2>
                <span className="advanced-filter-subtitle">
                  {t('adminReport.filterSubtitle')}
                </span>
              </div>
              <div className="advanced-filter-content">
                <div className="advanced-filter-group">
                  <span className="advanced-filter-label">{t('adminReport.quickPeriods')}</span>
                  <div className="chart-period-selector">
                    <button
                      className={`period-btn ${activePeriod === '7j' ? 'active' : ''}`}
                      onClick={() => {
                        setFilterError('');
                        setActivePeriod('7j');
                      }}
                    >
                      {t('adminReport.period7')}
                    </button>
                    <button
                      className={`period-btn ${activePeriod === '30j' ? 'active' : ''}`}
                      onClick={() => {
                        setFilterError('');
                        setActivePeriod('30j');
                      }}
                    >
                      {t('adminReport.period30')}
                    </button>
                    <button
                      className={`period-btn ${activePeriod === '90j' ? 'active' : ''}`}
                      onClick={() => {
                        setFilterError('');
                        setActivePeriod('90j');
                      }}
                    >
                      {t('adminReport.period90')}
                    </button>
                  </div>
                </div>
                <div className="advanced-filter-group">
                  <span className="advanced-filter-label">{t('adminReport.dateRange')}</span>
                  <div className="advanced-filter-fields">
                    <div className="advanced-filter-field">
                      <label htmlFor="rapport-date-from">{t('adminReport.from')}</label>
                      <input
                        id="rapport-date-from"
                        type="date"
                        value={customDateFromInput}
                        onChange={(e) => setCustomDateFromInput(e.target.value)}
                      />
                    </div>
                    <div className="advanced-filter-field">
                      <label htmlFor="rapport-date-to">{t('adminReport.to')}</label>
                      <input
                        id="rapport-date-to"
                        type="date"
                        value={customDateToInput}
                        onChange={(e) => setCustomDateToInput(e.target.value)}
                      />
                    </div>
                  </div>
                </div>
                <div className="advanced-filter-actions">
                  <button
                    className="filter-action-btn primary"
                    type="button"
                    onClick={applyCustomRange}
                  >
                    {t('adminReport.apply')}
                  </button>
                  <button
                    className="filter-action-btn secondary"
                    type="button"
                    onClick={resetFilters}
                  >
                    {t('adminReport.reset')}
                  </button>
                </div>
              </div>
              {filterError && <div className="advanced-filter-error">{filterError}</div>}
            </div>

            {/* KPI Cards Row */}
            <div className="kpi-row">
              <div className="kpi-card">
                <div className="kpi-card-header">
                  <div className="kpi-header-content">
                    <CurrencyDollarIcon className="kpi-icon" style={{ width: '24px', height: '24px', color: '#10B981' }} />
                    <h3 className="kpi-title">{t('adminReport.totalSales')}</h3>
                  </div>
                  <span className="kpi-index">#1</span>
                </div>
                <div className="kpi-value">{loading ? '...' : formatPrice(stats.totalSales)}</div>
                <div className="kpi-period">{t('adminReport.selectedPeriod')}</div>
              </div>

              <div className="kpi-card">
                <div className="kpi-card-header">
                  <div className="kpi-header-content">
                    <ShoppingCartIcon className="kpi-icon" style={{ width: '24px', height: '24px', color: '#8C1843' }} />
                    <h3 className="kpi-title">{t('adminReport.orders')}</h3>
                  </div>
                  <span className="kpi-index">#2</span>
                </div>
                <div className="kpi-value">{loading ? '...' : stats.totalOrders.toLocaleString('fr-FR')}</div>
                <div className="kpi-period">{t('adminReport.selectedPeriod')}</div>
              </div>

              <div className="kpi-card">
                <div className="kpi-card-header">
                  <div className="kpi-header-content">
                    <UserGroupIcon className="kpi-icon" style={{ width: '24px', height: '24px', color: '#3B82F6' }} />
                    <h3 className="kpi-title">{t('adminReport.users')}</h3>
                  </div>
                  <span className="kpi-index">#3</span>
                </div>
                <div className="kpi-value">{loading ? '...' : stats.totalUsers.toLocaleString('fr-FR')}</div>
                <div className="kpi-period">{t('adminReport.totalClients')}</div>
              </div>

              <div className="kpi-card">
                <div className="kpi-card-header">
                  <div className="kpi-header-content">
                    <ChartBarIcon className="kpi-icon" style={{ width: '24px', height: '24px', color: '#8C1843' }} />
                    <h3 className="kpi-title">{t('adminReport.conversionRate')}</h3>
                  </div>
                  <span className="kpi-index">#4</span>
                </div>
                <div className="kpi-value">{loading ? '...' : stats.conversionRate.toFixed(2)}%</div>
                <div className="kpi-period">{t('adminReport.selectedPeriod')}</div>
              </div>
            </div>

            {/* KPI Cards Row 2 */}
            <div className="kpi-row" style={{ marginTop: '24px' }}>
              <div className="kpi-card">
                <div className="kpi-card-header">
                  <div className="kpi-header-content">
                    <ShoppingBagIcon className="kpi-icon" style={{ width: '24px', height: '24px', color: '#10B981' }} />
                    <h3 className="kpi-title">{t('adminReport.averageCart')}</h3>
                  </div>
                  <span className="kpi-index">#5</span>
                </div>
                <div className="kpi-value">{loading ? '...' : formatPrice(stats.averageCart)}</div>
                <div className="kpi-period">{t('adminReport.selectedPeriod')}</div>
              </div>

              <div className="kpi-card">
                <div className="kpi-card-header">
                  <div className="kpi-header-content">
                    <ArrowPathIcon className="kpi-icon" style={{ width: '24px', height: '24px', color: '#F59E0B' }} />
                    <h3 className="kpi-title">{t('adminReport.bounceRate')}</h3>
                  </div>
                  <span className="kpi-index">#6</span>
                </div>
                <div className="kpi-value">{loading ? '...' : stats.bounceRate.toFixed(1)}%</div>
                <div className="kpi-period">{t('adminReport.selectedPeriod')}</div>
              </div>

              <div className="kpi-card">
                <div className="kpi-card-header">
                  <div className="kpi-header-content">
                    <ClockIcon className="kpi-icon" style={{ width: '24px', height: '24px', color: '#3B82F6' }} />
                    <h3 className="kpi-title">{t('adminReport.avgSessionTime')}</h3>
                  </div>
                  <span className="kpi-index">#7</span>
                </div>
                <div className="kpi-value">
                  {loading ? '...' : `${Math.floor(stats.averageSessionTime / 60)}m ${Math.floor(stats.averageSessionTime % 60)}s`}
                </div>
                <div className="kpi-period">{t('adminReport.selectedPeriod')}</div>
              </div>

              <div className="kpi-card">
                <div className="kpi-card-header">
                  <div className="kpi-header-content">
                    <DocumentTextIcon className="kpi-icon" style={{ width: '24px', height: '24px', color: '#8C1843' }} />
                    <h3 className="kpi-title">{t('adminReport.pageViews')}</h3>
                  </div>
                  <span className="kpi-index">#8</span>
                </div>
                <div className="kpi-value">{loading ? '...' : stats.pageViews.toLocaleString('fr-FR')}</div>
                <div className="kpi-period">{t('adminReport.selectedPeriod')}</div>
              </div>
            </div>

            {/* Charts Row 1 */}
            <div className="charts-row">
              <div className="chart-card">
                <div className="chart-header">
                  <h3 className="chart-title">{t('adminReport.salesByPeriod')}</h3>
                  <div className="chart-period-selector">
                    <button className={`period-btn ${activePeriod === '7j' ? 'active' : ''}`} onClick={() => setActivePeriod('7j')}>{t('adminReport.period7')}</button>
                    <button className={`period-btn ${activePeriod === '30j' ? 'active' : ''}`} onClick={() => setActivePeriod('30j')}>{t('adminReport.period30')}</button>
                    <button className={`period-btn ${activePeriod === '90j' ? 'active' : ''}`} onClick={() => setActivePeriod('90j')}>{t('adminReport.period90')}</button>
                  </div>
                </div>
                <div className="chart-container">
                  {loading ? (
                    <div style={{ width: '100%', textAlign: 'center', color: '#999', padding: '40px' }}>
                      {t('adminReport.loading')}
                    </div>
                  ) : timeSeries.length === 0 ? (
                    <div style={{ width: '100%', textAlign: 'center', color: '#999', padding: '40px' }}>
                      {t('adminReport.noData')}
                    </div>
                  ) : (
                    <canvas ref={salesChartRef} id="salesChart"></canvas>
                  )}
                </div>
              </div>

              <div className="chart-card">
                <div className="chart-header">
                  <h3 className="chart-title">{t('adminReport.salesDistribution')}</h3>
                </div>
                <div className="chart-container">
                  {loading ? (
                    <div style={{ width: '100%', textAlign: 'center', color: '#999', padding: '40px' }}>
                      {t('adminReport.loading')}
                    </div>
                  ) : orders.length === 0 ? (
                    <div style={{ width: '100%', textAlign: 'center', color: '#999', padding: '40px' }}>
                      {t('adminReport.noData')}
                    </div>
                  ) : (
                    <canvas ref={salesDistChartRef} id="salesDistributionChart"></canvas>
                  )}
                </div>
              </div>
            </div>

            {/* Charts Row 2 */}
            <div className="charts-row">
              <div className="chart-card">
                <div className="chart-header">
                  <h3 className="chart-title">{t('adminReport.ordersByStatus')}</h3>
                </div>
                <div className="chart-container">
                  {loading ? (
                    <div style={{ width: '100%', textAlign: 'center', color: '#999', padding: '40px' }}>
                      {t('adminReport.loading')}
                    </div>
                  ) : orders.length === 0 ? (
                    <div style={{ width: '100%', textAlign: 'center', color: '#999', padding: '40px' }}>
                      {t('adminReport.noData')}
                    </div>
                  ) : (
                    <canvas ref={ordersStatusChartRef} id="ordersStatusChart"></canvas>
                  )}
                </div>
              </div>

              <div className="chart-card">
                <div className="chart-header">
                  <h3 className="chart-title">{t('adminReport.top5Products')}</h3>
                </div>
                <div className="chart-container">
                  {loading ? (
                    <div style={{ width: '100%', textAlign: 'center', color: '#999', padding: '40px' }}>
                      {t('adminReport.loading')}
                    </div>
                  ) : orders.length === 0 ? (
                    <div style={{ width: '100%', textAlign: 'center', color: '#999', padding: '40px' }}>
                      {t('adminReport.noData')}
                    </div>
                  ) : (
                    <canvas ref={topProductsChartRef} id="topProductsChart"></canvas>
                  )}
                </div>
              </div>
            </div>

            {/* Charts Row 3 */}
            <div className="charts-row">
              <div className="chart-card chart-card-large">
                <div className="chart-header">
                  <h3 className="chart-title">{t('adminReport.revenueEvolution')}</h3>
                  <div className="chart-period-selector">
                    <button className={`period-btn ${activePeriod === '7j' ? 'active' : ''}`} onClick={() => setActivePeriod('7j')}>{t('adminReport.period7')}</button>
                    <button className={`period-btn ${activePeriod === '30j' ? 'active' : ''}`} onClick={() => setActivePeriod('30j')}>{t('adminReport.period30')}</button>
                    <button className={`period-btn ${activePeriod === '90j' ? 'active' : ''}`} onClick={() => setActivePeriod('90j')}>{t('adminReport.period90')}</button>
                  </div>
                </div>
                <div className="chart-container">
                  {loading ? (
                    <div style={{ width: '100%', textAlign: 'center', color: '#999', padding: '40px' }}>
                      {t('adminReport.loading')}
                    </div>
                  ) : (
                    <canvas ref={revenueChartRef} id="revenueChart"></canvas>
                  )}
                </div>
              </div>
            </div>

            {/* Charts Row 4 - Nouveaux graphiques */}
            <div className="charts-row">
              <div className="chart-card">
                <div className="chart-header">
                  <h3 className="chart-title">{t('adminReport.conversionRateChart')}</h3>
                </div>
                <div className="chart-container">
                  {loading ? (
                    <div style={{ width: '100%', textAlign: 'center', color: '#999', padding: '40px' }}>
                      {t('adminReport.loading')}
                    </div>
                  ) : (
                    <canvas ref={conversionChartRef} id="conversionChart"></canvas>
                  )}
                </div>
              </div>

              <div className="chart-card">
                <div className="chart-header">
                  <h3 className="chart-title">{t('adminReport.avgCartValue')}</h3>
                </div>
                <div className="chart-container">
                  {loading ? (
                    <div style={{ width: '100%', textAlign: 'center', color: '#999', padding: '40px' }}>
                      {t('adminReport.loading')}
                    </div>
                  ) : (
                    <canvas ref={cartValueChartRef} id="cartValueChart"></canvas>
                  )}
                </div>
              </div>
            </div>

            {/* Charts Row 5 */}
            <div className="charts-row">
              <div className="chart-card chart-card-large">
                <div className="chart-header">
                  <h3 className="chart-title">{t('adminReport.sessionsAndCarts')}</h3>
                </div>
                <div className="chart-container">
                  {loading ? (
                    <div style={{ width: '100%', textAlign: 'center', color: '#999', padding: '40px' }}>
                      {t('adminReport.loading')}
                    </div>
                  ) : (
                    <canvas ref={sessionsChartRef} id="sessionsChart"></canvas>
                  )}
                </div>
              </div>
            </div>
  
    </>
  );
};
